import {useQuery} from '@tanstack/react-query';
import {Alert, Box, CircularProgress, Typography} from '@mui/material';
import Indicator from '../../components/Indicator';
import MultiIndicator from '../../components/MultiIndicator';
import NumericPair from '../../components/NumericPair';
import Section from '../../components/Section';
import Legend from '../../components/Legend';
import {fetchSlugs} from '../utils/helpers';
import {
    ACTIVITY_LABELS,
    AGE_GROUPS,
    LuogoSurveys,
    PHASE_LABELS,
    TOOLTIPS,
    buildLuogoSlugs,
} from '../utils/slugs';
import {DashboardData} from '../utils/types';

interface Props {
    container: string;
    uoaId: string;
    surveys: LuogoSurveys;
}

const BOX_LABELS = {uoa: 'Il mio progetto', average: 'Media dei progetti'};
const TARGET_SERIES_LABELS = {uoa: 'Il mio progetto', average: 'Valore complessivo'};

const METHOD_NOTE =
    'Nota metodologica: i valori mancanti non sono assimilati a zero. Nei casi in cui il denominatore sia pari a zero, l’indicatore è restituito come non calcolabile.';

const seriesItems = (data: DashboardData, prefix: string, labels: string[]) =>
    labels.map((label, i) => ({
        label,
        uoa: data[`${prefix}_${i + 1}`].uoa,
        average: data[`${prefix}_${i + 1}`].average,
    }));

const Luogo = ({container, uoaId, surveys}: Props) => {
    const {data, isLoading, isError} = useQuery<DashboardData>({
        queryKey: ['luoghi-2025-luogo', container, uoaId, surveys],
        queryFn: () => fetchSlugs(container, uoaId, buildLuogoSlugs(surveys)),
        staleTime: 5 * 60 * 1000,
    });

    if (isLoading) {
        return (
            <Box sx={{display: 'flex', justifyContent: 'center', py: 8}}>
                <CircularProgress size={48} />
            </Box>
        );
    }

    if (isError || !data) {
        return <Alert severity='error'>Errore nel caricamento dei dati.</Alert>;
    }

    return (
        <Box>
            <Typography variant='caption' color='text.disabled' sx={{display: 'block', mb: 2}}>
                {METHOD_NOTE}
            </Typography>

            <Legend />

            <Section title='I. SUPERFICIE RIGENERATA SU TOTALE DEL LUOGO'>
                <NumericPair
                    title='Percentuale di superficie oggetto di rigenerazione a confronto con la media'
                    tooltip={TOOLTIPS.superficie}
                    uoaValue={data.superficie.uoa}
                    averageValue={data.superficie.average}
                    labels={BOX_LABELS}
                />
            </Section>

            <Section title='II. COMPLESSITÀ TECNICA DELL’INTERVENTO'>
                <NumericPair
                    title='Complessità tecnica prevista dell’intervento a confronto con la media'
                    tooltip={TOOLTIPS.complessita}
                    uoaValue={data.complessita.uoa}
                    averageValue={data.complessita.average}
                    isCount
                    labels={BOX_LABELS}
                />
            </Section>

            <Section title='III. AVANZAMENTO DEI LAVORI'>
                <NumericPair
                    title='Percentuale di avanzamento dei lavori a confronto con la media'
                    tooltip={TOOLTIPS.avanzamento}
                    uoaValue={data.avanzamento.uoa}
                    averageValue={data.avanzamento.average}
                    labels={BOX_LABELS}
                />
            </Section>

            <Section title='IV. PARTECIPAZIONE CITTADINA'>
                <NumericPair
                    title='Partecipazione cittadina a confronto con la media'
                    tooltip={TOOLTIPS.partecipazione}
                    uoaValue={data.partecipazione.uoa}
                    averageValue={data.partecipazione.average}
                    isCount
                    labels={BOX_LABELS}
                />
            </Section>

            <Section title='V. FASI DI COINVOLGIMENTO'>
                <MultiIndicator
                    title='Coinvolgimento nelle fasi di progettazione a confronto con la media'
                    tooltip={TOOLTIPS.fasi}
                    items={seriesItems(data, 'fase', PHASE_LABELS)}
                    maxOverride={100}
                />
            </Section>

            <Section title='VI. ATTIVITÀ PARTECIPATIVE REALIZZATE'>
                <MultiIndicator
                    title='Tipologie di attività partecipative a confronto con la media'
                    tooltip={TOOLTIPS.attivita}
                    items={seriesItems(data, 'attivita', ACTIVITY_LABELS)}
                    maxOverride={100}
                />
            </Section>

            <Section title='VII. APERTURA EFFETTIVA DEL LUOGO'>
                <NumericPair
                    title='Numero di giornate di apertura a confronto con la media'
                    tooltip={TOOLTIPS.apertura}
                    uoaValue={data.apertura.uoa}
                    averageValue={data.apertura.average}
                    isCount
                    labels={BOX_LABELS}
                />
            </Section>

            <Section title='VIII. TASSO DI RAGGIUNGIMENTO DEI FRUITORI ATTESI'>
                <NumericPair
                    title='Tasso di raggiungimento dei fruitori attesi a confronto con la media'
                    tooltip={TOOLTIPS.fruitori}
                    uoaValue={data.fruitori.uoa}
                    averageValue={data.fruitori.average}
                    labels={BOX_LABELS}
                />
            </Section>

            <Section title='IX. TARGET DELLE INIZIATIVE'>
                <MultiIndicator
                    title='Target di età delle iniziative a confronto con il valore complessivo'
                    tooltip={TOOLTIPS.target}
                    items={seriesItems(data, 'target', AGE_GROUPS)}
                    seriesLabels={TARGET_SERIES_LABELS}
                    maxOverride={100}
                />
            </Section>

            <Section title='X. FREQUENZA DI FRUIZIONE DEL LUOGO'>
                <Indicator
                    roman=''
                    title='Frequenza di fruizione del luogo a confronto con la media'
                    tooltip={TOOLTIPS.frequenza}
                    data={data.frequenza}
                    maxOverride={100}
                />
            </Section>

            <Section title='XI. INTERESSE DEI FRUITORI A TORNARE PRESSO I LUOGHI'>
                <Indicator
                    roman=''
                    title='Interesse a tornare presso il luogo a confronto con la media'
                    tooltip={TOOLTIPS.ritorno}
                    data={data.ritorno}
                    maxOverride={100}
                />
            </Section>
        </Box>
    );
};

export default Luogo;
